import videojs from 'video.js';

/** @type {WeakMap<AudioDescriptionsPlayer, boolean>} */
const _enabled = new WeakMap();

const POOR_SYNC = 0.5; // seconds
const VERY_BAD_SYNC = 1.0; // seconds
const SYNC_CORRECTION = 0.1;

export default class AudioDescriptionsPlayer extends videojs.getComponent('Component') {
    /**
     * 
     * @param {videojs.Player} player
     * @param {any} options
     */
    constructor(player, options) {
        super(player, options);
    }

    createEl(tag, props = {}, attributes = {}) {
        tag = 'audio';
        props = Object.assign({});
        attributes = Object.assign({});

        return super.createEl.call(this, tag, props, attributes);
    }

    /**
     * @type {boolean}
     */
    set enabled(enabled) {
        _enabled.set(this, enabled);
        this.el().muted = !enabled;
        if (enabled) {
            this.el().volume = this.player().volume();
            this.el().muted = this.player().muted();
        }
    }
    
    get enabled() {
        return _enabled.get(this);
    }

    set src(src) {
        const player = this.player();

        if (src) {
            this.el().src = src;

            this.handlePlayerSeeking();
            if (player.paused()) {
                this.el().pause();
            }
            else {
                this.el().play();
            }

            // Set up event listeners
            this.on(player, 'seeking', this.handlePlayerSeeking);
            this.on(player, 'timeupdate', this.handlePlayerTimeupdate);
            this.on(player, 'playing', this.handlePlayerPlaying);
            this.on(player, 'pause', this.handlePlayerPause);
            this.on(player, 'ratechange', this.handlePlayerRatechange);
            this.on(player, 'volumechange', this.handlePlayerVolumeChange);
        }
        else {
            this.el().pause();
            this.el().removeAttribute('src');

            this.off(player, 'seeking', this.handlePlayerSeeking);
            this.off(player, 'timeupdate', this.handlePlayerTimeupdate);
            this.off(player, 'playing', this.handlePlayerPlaying);
            this.off(player, 'pause', this.handlePlayerPause);
            this.off(player, 'ratechange', this.handlePlayerRatechange);
            this.off(player, 'volumechange', this.handlePlayerVolumeChange);
        }
    }

    get src() {
        return this.el().src;
    }

    /**
     * @param {Event} event
     */
    handlePlayerSeeking(event) {
        this.el().currentTime = this.player().currentTime();
    }

    /**
     * @param {Event} event
     */
    handlePlayerTimeupdate(event) {
        const playerTime = this.player().currentTime();
        const playerRate = this.player().playbackRate();
        const audioTime = this.el().currentTime;
        const diff = audioTime - playerTime;

        if (Math.abs(diff) > VERY_BAD_SYNC) {
            this.handlePlayerSeeking();
        }
        else {
            const currentPlaybackRate = this.el().playbackRate;
            let correctedRate = playerRate;
            if (diff > POOR_SYNC) {
                correctedRate = playerRate - SYNC_CORRECTION;
            }
            else if (diff < -POOR_SYNC) {
                correctedRate = playerRate + SYNC_CORRECTION;
            }

            if (correctedRate !== currentPlaybackRate) {
                this.el().playbackRate = correctedRate;
                this.player().audioDescriptions().log(`Adjusting audio descriptions playback rate: ${correctedRate}`);
            }
        }
    }

    /**
     * @param {Event} event
     */
    handlePlayerPlaying(event) {
        this.el().play();
    }

    /**
     * @param {Event} event
     */
    handlePlayerPause(event) {
        this.el().pause();
        this.handlePlayerSeeking();
    }

    /**
     * @param {Event} event
     */
    handlePlayerRatechange(event) {
        this.el().playbackRate = this.player().playbackRate();
    }

    /**
     * @param {Event} event
     */
    handlePlayerVolumeChange(event) {
        if (_enabled.get(this)) {
            this.el().volume = this.player().volume();
            this.el().muted = this.player().muted();
        }
    }
}
